import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, FileText, Download, Eye, AlertTriangle, Check, Star } from "lucide-react";

const CampoInput = ({ c, valore, onChange }) => {
  const base = "w-full px-3 py-2 text-sm border border-[#1A2B4A]/15 rounded-md focus:outline-none focus:border-[#C9A84C]";
  const tid = `campo-${c.chiave}`;
  if (c.tipo === "testo_lungo")
    return <textarea data-testid={tid} rows={3} className={base} value={valore || ""} onChange={(e) => onChange(e.target.value)} />;
  if (c.tipo === "lista_testo")
    return <textarea data-testid={tid} rows={4} className={base} placeholder="una voce per riga"
      value={Array.isArray(valore) ? valore.join("\n") : valore || ""}
      onChange={(e) => onChange(e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} />;
  if (c.tipo === "scelta")
    return (
      <select data-testid={tid} className={base} value={valore || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {c.opzioni.map((o) => <option key={o} value={o}>{c.etichette_opzioni?.[o] || o}</option>)}
      </select>
    );
  if (c.tipo === "booleano")
    return (
      <label className="inline-flex items-center gap-2 text-sm text-[#1A2B4A]">
        <input type="checkbox" data-testid={tid} checked={!!valore} onChange={(e) => onChange(e.target.checked)} className="accent-[#C9A84C]" />
        {valore ? "Sì" : "No"}
      </label>
    );
  if (c.tipo === "tabella") {
    const righe = Array.isArray(valore) ? valore : [];
    const cambia = (i, col, v) => onChange(righe.map((r, k) => k === i ? { ...r, [col]: v } : r));
    return (
      <div data-testid={tid} className="space-y-1">
        {righe.map((r, i) => (
          <div key={i} className="flex gap-1">
            {c.colonne.map((col) => (
              <input key={col} value={r[col] || ""} placeholder={col} onChange={(e) => cambia(i, col, e.target.value)}
                className="flex-1 min-w-0 px-2 py-1.5 text-[12px] border border-[#1A2B4A]/15 rounded-md" />
            ))}
            <button onClick={() => onChange(righe.filter((_, k) => k !== i))} className="px-2 text-[#9b2c2c]">×</button>
          </div>
        ))}
        <button data-testid={`${tid}-aggiungi`} onClick={() => onChange([...righe, {}])}
          className="text-[11px] text-[#1A2B4A] underline">+ aggiungi riga</button>
      </div>
    );
  }
  return <input data-testid={tid} className={base} placeholder={c.tipo === "data" ? "GG/MM/AAAA" : ""}
    inputMode={c.tipo === "euro" || c.tipo === "numero" ? "decimal" : "text"}
    value={valore ?? ""} onChange={(e) => onChange(e.target.value)} />;
};

const PASSAGGI = {
  impostazioni: "Impostazioni > Documenti",
  drive_template: "lettura dei template su Drive",
  motore: "motore documenti (Node)",
  conversione: "conversione in PDF (LibreOffice)",
  drive_salvataggio: "salvataggio su Drive",
  generazione: "generazione",
};

export default function GeneraDocumentoDialog({ open, onClose, clienteId, clienteNome, praticaId }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [forza, setForza] = useState(false);
  const [notaForza, setNotaForza] = useState("");
  const [cliente, setCliente] = useState(clienteId || "");
  const [pratica, setPratica] = useState(praticaId || "");
  const [passo, setPasso] = useState(clienteId ? (praticaId ? 2 : 1) : 1);
  const [codice, setCodice] = useState("");
  const [modulo, setModulo] = useState(null);
  const [dati, setDati] = useState({});
  const [allegati, setAllegati] = useState([]);
  const [busy, setBusy] = useState("");
  const [mancanti, setMancanti] = useState([]);
  const [senzaDoc, setSenzaDoc] = useState(false);
  const [motivoDoc, setMotivoDoc] = useState("");
  const [guasto, setGuasto] = useState(null);
  const [esito, setEsito] = useState(null);
  const [conferme, setConferme] = useState([]);

  const { data: clienti } = useQuery({
    queryKey: ["doc-clienti"], enabled: open && !clienteId,
    queryFn: async () => (await api.get("/clienti")).data,
  });
  const { data: pratiche } = useQuery({
    queryKey: ["doc-pratiche", cliente], enabled: open && !!cliente && !praticaId,
    queryFn: async () => (await api.get("/pratiche")).data.filter((p) => p.cliente_id === cliente),
  });
  const { data: catalogo, isLoading: caricaCat, error: errCat } = useQuery({
    queryKey: ["doc-catalogo", cliente, pratica], enabled: open && passo === 2 && !!cliente,
    queryFn: async () => (await api.get("/documenti/catalogo", { params: { cliente_id: cliente, pratica_id: pratica || undefined } })).data,
  });

  // il dialog resta montato: quando si apre, riallinea cliente e pratica a quelli ricevuti
  useEffect(() => {
    setCliente(clienteId || "");
    setPratica(praticaId || "");
    setPasso(clienteId ? (praticaId ? 2 : 1) : 1);
    if (!open) { setCodice(""); setModulo(null); setEsito(null); setMancanti([]); setGuasto(null); }
  }, [open, clienteId, praticaId]);

  const apriModulo = async (cod) => {
    setBusy("modulo"); setCodice(cod); setMancanti([]); setEsito(null);
    setSenzaDoc(false); setMotivoDoc("");
    try {
      const { data } = await api.post("/documenti/modulo", { codice: cod, cliente_id: cliente, pratica_id: pratica || undefined });
      setModulo(data);
      setAllegati(data.allegati);
      const v = {};
      data.sezioni.forEach((s) => s.campi.forEach((c) => { v[c.chiave] = c.valore ?? ""; }));
      setDati(v);
      setPasso(3);
    } catch (e) { toast.error(e?.response?.data?.detail || "Modello non disponibile"); }
    finally { setBusy(""); }
  };

  const cambiaCampo = async (chiave, valore) => {
    const nuovi = { ...dati, [chiave]: valore };
    setDati(nuovi);
    if (chiave === "DOC_CANALE") {
      try {
        const { data } = await api.post("/documenti/allegati", {
          codice, cliente_id: cliente, pratica_id: pratica || undefined,
          allegati: allegati.filter((a) => a.spuntato).map((a) => a.codice), dati: nuovi,
        });
        setAllegati(data.allegati);
      } catch { /* la casella resta com'è */ }
    }
  };

  const CALCOLATI_DAL_MOTORE = { M01: ["FASI"], M02: ["FASI"], M09: ["FASI"] };

  const vuoti = () => {
    const esenti = CALCOLATI_DAL_MOTORE[codice] || [];
    const out = [];
    (modulo?.sezioni || []).forEach((s2) => s2.campi.forEach((c) => {
      if (!c.obbligatorio || esenti.includes(c.chiave)) return;
      if (c.obbligatorio_se && !(!!dati[c.obbligatorio_se.replace("!", "")] === !c.obbligatorio_se.startsWith("!"))) return;
      const v = dati[c.chiave];
      const pieno = Array.isArray(v) ? v.length > 0 : String(v ?? "").trim() !== "";
      if (!pieno) out.push({ campo: c.chiave, etichetta: c.etichetta });
    }));
    return out;
  };

  const genera = async (scarica) => {
    const senzaValore = vuoti();
    if (senzaValore.length && !(forza && can("gestione_impostazioni"))) {
      setMancanti(senzaValore);
      document.querySelector(`[data-testid="campo-${senzaValore[0].campo}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      toast.error(`Compila prima ${senzaValore.length === 1 ? "il campo obbligatorio" : `i ${senzaValore.length} campi obbligatori`}: ${senzaValore.map((m) => m.etichetta).join(" · ")}`);
      return;
    }
    setBusy(scarica ? "salva" : "anteprima"); setMancanti([]); setGuasto(null);
    try {
      const { data } = await api.post("/documenti/genera", {
        codice, cliente_id: cliente, pratica_id: pratica || undefined,
        variante: modulo.variante, allegati: allegati.filter((a) => a.spuntato).map((a) => a.codice), dati,
        salva: !!scarica,
        forzato: senzaValore.length > 0 && forza,
        forzatura_nota: notaForza,
        campi_forzati: senzaValore.map((m) => m.chiave || m.campo),
        senza_documenti: docMancanti.length > 0 && senzaDoc,
        motivo_documenti: motivoDoc,
      });
      setEsito(data);
      setConferme([]);
      const blob = new Blob([Uint8Array.from(atob(data.pdf_base64), (c) => c.charCodeAt(0))], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (scarica) {
        const a = document.createElement("a");
        a.href = url; a.download = data.nome_file; a.click();
      } else window.open(url, "_blank");
      if (scarica) {
        qc.invalidateQueries({ queryKey: ["documenti-generati"] });
        qc.invalidateQueries({ queryKey: ["clienti"] });
      }
      if (data.errore_drive) toast.error(data.errore_drive);
      else toast.success(scarica
        ? `Salvato su Drive · ${data.nome_file}`
        : `Anteprima pronta · ${data.sezioni.join(" · ")}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.errore === "campi_obbligatori") {
        setMancanti(d.mancanti);
        const primo = document.querySelector(`[data-testid="campo-${d.mancanti[0].campo}"]`);
        primo?.scrollIntoView({ behavior: "smooth", block: "center" });
        toast.error(`Mancano ${d.mancanti.length} campi obbligatori`);
      } else if (d && typeof d === "object" && (d.passaggio || d.dettaglio_tecnico)) {
        setGuasto(d);
        toast.error(d.detail || "Generazione non riuscita");
      } else toast.error(typeof d === "string" ? d : "Generazione non riuscita");
    } finally { setBusy(""); }
  };

  const riprovaSalvataggio = async () => {
    if (!esito?.pdf_base64 || !esito?.registro?.id) return;
    setBusy("riprova");
    const bytes = Uint8Array.from(atob(esito.pdf_base64), (c) => c.charCodeAt(0));
    const fd = new FormData();
    fd.append("file", new File([bytes], esito.nome_file, { type: "application/pdf" }));
    try {
      await api.post(`/documenti/generati/${esito.registro.id}/riprova`, fd);
      toast.success("Salvato su Drive");
      setEsito({ ...esito, errore_drive: "" });
      qc.invalidateQueries({ queryKey: ["documenti-generati"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally { setBusy(""); }
  };

  const applicaAnagrafica = async () => {
    try {
      const { data } = await api.post("/documenti/anagrafica", { cliente_id: cliente, campi: conferme });
      toast.success(`Anagrafica aggiornata: ${data.aggiornati.join(", ")}`);
      setEsito({ ...esito, anagrafica_da_confermare: [] });
    } catch { toast.error("Aggiornamento non riuscito"); }
  };

  if (!open) return null;
  const mancante = (k) => mancanti.some((m) => m.campo === k);
  const docMancanti = modulo?.documenti_mancanti || [];
  const bloccoDoc = docMancanti.length > 0 && !senzaDoc;
  const modSelezionato = (catalogo?.gruppi || []).flatMap((g) => g.modelli).find((m) => m.codice === codice);

  return (
    <div className="fixed inset-0 z-50 bg-[#16233c]/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="genera-documento-dialog">
      <div className="bento bg-white w-full max-w-3xl my-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1A2B4A]/10">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-[#8a93a6]">Passaggio {passo} di 3</p>
            <h3 className="text-lg font-semibold text-[#1A2B4A]">
              {passo === 1 ? "Cliente e pratica" : passo === 2 ? "Scegli il modello" : modulo?.titolo}
            </h3>
          </div>
          <button data-testid="documento-chiudi" onClick={onClose} className="text-[#1A2B4A]/60 hover:text-[#1A2B4A]"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5">
          {passo === 1 && (
            <div className="space-y-4">
              {!clienteId && (
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Cliente</label>
                  <select data-testid="doc-cliente" value={cliente} onChange={(e) => { setCliente(e.target.value); setPratica(""); }}
                    className="w-full mt-1 px-3 py-2 text-sm border border-[#1A2B4A]/15 rounded-md">
                    <option value="">Scegli…</option>
                    {(clienti || []).map((c) => <option key={c.id} value={c.id}>{c.cognome} {c.nome}{c.is_azienda ? " · impresa" : ""}</option>)}
                  </select>
                </div>
              )}
              {clienteId && <p className="text-sm text-[#1A2B4A]">Cliente: <strong>{clienteNome}</strong></p>}
              {!!cliente && !praticaId && (
                <div>
                  <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Per quale pratica?</label>
                  <select data-testid="doc-pratica" value={pratica} onChange={(e) => setPratica(e.target.value)}
                    className="w-full mt-1 px-3 py-2 text-sm border border-[#1A2B4A]/15 rounded-md">
                    <option value="">Nessuna pratica</option>
                    {(pratiche || []).map((p) => <option key={p.id} value={p.id}>{p.titolo || p.tipo_pratica_nome}</option>)}
                  </select>
                </div>
              )}
              <button data-testid="doc-avanti" disabled={!cliente} onClick={() => setPasso(2)}
                className="px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-md disabled:opacity-50">Avanti</button>
            </div>
          )}

          {passo === 2 && (
            <div className="space-y-5">
              {caricaCat && <p className="text-sm text-[#4a5568]">Leggo il catalogo dei modelli da Drive…</p>}
              {errCat && <p className="text-sm text-[#9b2c2c]">{errCat?.response?.data?.detail || "Catalogo non disponibile"}</p>}
              {catalogo?.motivo && (
                <div data-testid="doc-motivo" className="rounded-md bg-[#f4f4f5] border border-[#1A2B4A]/10 px-3 py-2 text-[12px] text-[#4a5568]">
                  {catalogo.motivo}
                </div>
              )}
              {(catalogo?.gruppi || []).map((g) => (
                <div key={g.gruppo} data-testid={`gruppo-${g.gruppo}`}>
                  <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-2">{g.gruppo}</p>
                  {g.modelli.length === 0 && <p className="text-[12px] text-[#8a93a6]">Nessun modello disponibile.</p>}
                  <div className="grid sm:grid-cols-2 gap-2">
                    {g.modelli.map((m) => (
                      <button key={m.codice} data-testid={`modello-${m.codice}`} onClick={() => apriModulo(m.codice)} disabled={!!busy}
                        className="text-left px-3 py-2.5 rounded-md border border-[#1A2B4A]/10 bg-[#f9f8f5] hover:border-[#C9A84C] transition-colors">
                        <span className="flex items-center gap-2 text-sm font-medium text-[#1A2B4A]">
                          <FileText className="w-4 h-4 text-[#C9A84C]" />{m.pulsante}
                          {m.consigliato && (
                            <span data-testid={`consigliato-${m.codice}`} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded-full bg-[#FAF5E8] text-[#7a5c12] border border-[#C9A84C]/50">
                              <Star className="w-3 h-3" /> Consigliato
                            </span>
                          )}
                          {m.fuori_catalogo && (
                            <span data-testid={`fuori-catalogo-${m.codice}`}
                              title="Non è il modello previsto per questo tipo pratica: puoi generarlo comunque"
                              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded-full bg-[#FDFAF1] text-[#7a5c12] border border-[#C9A84C]/50">
                              <AlertTriangle className="w-3 h-3" /> Fuori catalogo
                            </span>
                          )}
                        </span>
                        {m.descrizione && <span className="block text-[11px] text-[#4a5568] mt-1">{m.descrizione}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <button onClick={() => setPasso(1)} className="text-[12px] text-[#1A2B4A] underline">Indietro</button>
            </div>
          )}

          {passo === 3 && modulo && (
            <div className="space-y-5">
              <div data-testid="doc-nota-verde" className="rounded-md border border-[#276749]/30 bg-[#f0f6f2] px-3 py-2 text-[12px] text-[#1F5C42]">
                {modulo.testo_verde}
              </div>
              {modSelezionato?.fuori_catalogo && (
                <div data-testid="doc-fuori-catalogo"
                  className="rounded-md border border-[#C9A84C]/60 bg-[#FDFAF1] px-3 py-2 text-[12px] text-[#7a5c12]">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  Non è il modello previsto per questo tipo pratica. Puoi generarlo comunque.
                </div>
              )}
              {docMancanti.length > 0 && (
                <div data-testid="doc-checklist-incompleta"
                  className="rounded-md border border-[#C9A84C]/60 bg-[#FDFAF1] px-3 py-3 text-[12px] text-[#7a5c12] space-y-2">
                  <p>
                    <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                    <strong>Documentazione incompleta:</strong> mancano {docMancanti.join(", ")}.
                    Puoi generare comunque il documento.
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer font-medium">
                    <input data-testid="doc-senza-documenti" type="checkbox" checked={senzaDoc}
                      onChange={(e) => setSenzaDoc(e.target.checked)} className="accent-[#C9A84C]" />
                    Genero senza la documentazione completa
                  </label>
                  {senzaDoc && (
                    <input data-testid="doc-motivo-documenti" value={motivoDoc}
                      onChange={(e) => setMotivoDoc(e.target.value)}
                      placeholder="Motivo (facoltativo)"
                      className="w-full px-2 py-1.5 rounded-md border border-[#C9A84C]/50 bg-white text-[12px] text-[#1A2B4A] focus:border-[#C9A84C] outline-none" />
                  )}
                </div>
              )}
              {mancanti.length > 0 && (
                <div data-testid="doc-mancanti" className="rounded-md border border-[#9b2c2c]/30 bg-[#fdf3f3] px-3 py-2 text-[12px] text-[#8C2A2A]">
                  Mancano: {mancanti.map((m) => m.etichetta).join(" · ")}
                  {forza && <span className="block mt-1">Questi campi li pretende il motore del contratto: non posso lasciarli vuoti nemmeno forzando.</span>}
                </div>
              )}
              {can("gestione_impostazioni") && vuoti().length > 0 && (
                <div data-testid="doc-forzatura" className="flex flex-wrap items-center gap-2 text-[11px] text-[#8a93a6]">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer hover:text-[#1A2B4A]">
                    <input data-testid="doc-forzatura-spunta" type="checkbox" checked={forza}
                      onChange={(e) => setForza(e.target.checked)}
                      className="w-3 h-3 accent-[#C9A84C]" />
                    Procedi comunque con {vuoti().length} {vuoti().length === 1 ? "campo obbligatorio vuoto" : "campi obbligatori vuoti"}
                  </label>
                  {forza && (
                    <input data-testid="doc-forzatura-nota" value={notaForza} onChange={(e) => setNotaForza(e.target.value)}
                      placeholder="Nota, se vuoi spiegare il perché (facoltativa)"
                      className="flex-1 min-w-[220px] px-2 py-1 rounded-md border border-[#C9A84C]/50 bg-white text-[11px] text-[#1A2B4A] focus:border-[#C9A84C] outline-none" />
                  )}
                  {forza && <span className="text-[#8C2A2A]">Resta segnato sul documento e nel registro.</span>}
                </div>
              )}
              {modulo.sezioni.map((s) => (
                <div key={s.titolo}>
                  <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-2">{s.titolo}</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {s.campi.filter((c) => !c.obbligatorio_se || !!dati[c.obbligatorio_se.replace("!", "")] === !c.obbligatorio_se.startsWith("!"))
                      .map((c) => (
                        <div key={c.chiave} className={c.tipo === "tabella" || c.tipo === "lista_testo" || c.tipo === "testo_lungo" ? "sm:col-span-2" : ""}>
                          <label className={`text-[11px] font-medium ${mancante(c.chiave) ? "text-[#9b2c2c]" : "text-[#4a5568]"}`}>
                            {c.etichetta}{c.obbligatorio ? " *" : ""}
                          </label>
                          <div className={mancante(c.chiave) ? "ring-1 ring-[#9b2c2c] rounded-md" : ""}>
                            <CampoInput c={c} valore={dati[c.chiave]} onChange={(v) => cambiaCampo(c.chiave, v)} />
                          </div>
                          {c.aiuto && <p className={`text-[11px] mt-1 ${c.chiave === "DOC_CANALE" ? "text-[#7a5c12] bg-[#FDFAF1] border border-[#C9A84C]/40 rounded px-2 py-1" : "text-[#8a93a6]"}`}>{c.aiuto}</p>}
                        </div>
                      ))}
                  </div>
                </div>
              ))}

              {allegati.length > 0 && (
                <div data-testid="doc-allegati">
                  <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6] mb-2">Allegati</p>
                  <div className="space-y-2">
                    {allegati.map((a) => (
                      <div key={a.codice}>
                        <label className="inline-flex items-center gap-2 text-sm text-[#1A2B4A]">
                          <input type="checkbox" data-testid={`allegato-${a.codice}`} checked={a.spuntato} disabled={a.bloccato}
                            onChange={(e) => setAllegati(allegati.map((x) => x.codice === a.codice ? { ...x, spuntato: e.target.checked } : x))}
                            className="accent-[#C9A84C]" />
                          {a.pulsante}
                        </label>
                        {(a.nota || (a.codice === "D01" && !a.spuntato && modulo.variante === "C")) && (
                          <p data-testid={`allegato-nota-${a.codice}`} className="text-[11px] text-[#7a5c12] ml-6">
                            {a.bloccato ? a.nota : "L'informativa precontrattuale è obbligatoria: toglila solo se l'hai già consegnata"}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {guasto && (
                <div data-testid="doc-guasto" className="rounded-md border border-[#9b2c2c]/30 bg-[#FDECEC] px-3 py-2.5 text-[12px] text-[#9b2c2c]">
                  <p className="font-medium">{guasto.detail}</p>
                  {guasto.passaggio && (
                    <p className="mt-1 text-[11px]">Passaggio: <strong data-testid="doc-guasto-passaggio">{PASSAGGI[guasto.passaggio] || guasto.passaggio}</strong></p>
                  )}
                  {guasto.dettaglio_tecnico && (
                    <pre data-testid="doc-guasto-dettaglio" className="mt-1 text-[10px] whitespace-pre-wrap break-words bg-white/60 rounded p-2">{guasto.dettaglio_tecnico}</pre>
                  )}
                </div>
              )}
              {esito?.errore_drive && (
                <div data-testid="doc-errore-drive" className="rounded-md border border-[#9b2c2c]/30 bg-[#FDECEC] px-3 py-2.5 text-[12px] text-[#9b2c2c]">
                  <p>{esito.errore_drive} Il PDF è già stato scaricato sul tuo computer.</p>
                  {esito.dettaglio_tecnico && (
                    <pre data-testid="doc-guasto-dettaglio-drive" className="mt-1 text-[10px] whitespace-pre-wrap break-words bg-white/60 rounded p-2">Passaggio: {PASSAGGI.drive_salvataggio} · {esito.dettaglio_tecnico}</pre>
                  )}
                  <button data-testid="doc-riprova-salvataggio" onClick={riprovaSalvataggio} disabled={!!busy}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#9b2c2c] text-white rounded-md disabled:opacity-50">
                    {busy === "riprova" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Riprova salvataggio
                  </button>
                </div>
              )}
              {esito?.registro && !esito?.errore_drive && (
                <div data-testid="doc-salvato" className="rounded-md border border-[#1F5C42]/25 bg-[#E7F2EC] px-3 py-2 text-[12px] text-[#1F5C42]">
                  <Check className="w-3.5 h-3.5 inline mr-1" />
                  Salvato come <strong>{esito.registro.nome_file}</strong> nella cartella del cliente e nell'archivio
                  {esito.registro.link_drive_cliente && (
                    <> · <a href={esito.registro.link_drive_cliente} target="_blank" rel="noreferrer" className="underline">copia cliente</a></>
                  )}
                  {esito.registro.link_drive_archivio && (
                    <> · <a href={esito.registro.link_drive_archivio} target="_blank" rel="noreferrer" className="underline">copia archivio</a></>
                  )}
                </div>
              )}
              {esito?.avvisi?.length > 0 && (
                <div data-testid="doc-avvisi" className="rounded-md border border-[#C9A84C]/50 bg-[#FDFAF1] px-3 py-2 text-[12px] text-[#7a5c12]">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                  {esito.avvisi.join(" · ")}
                </div>
              )}
              {esito?.anagrafica_da_confermare?.length > 0 && (
                <div data-testid="doc-anagrafica" className="rounded-md border border-[#C9A84C]/50 bg-[#FDFAF1] px-3 py-3 text-[12px] text-[#7a5c12]">
                  <p className="font-medium mb-1">Aggiorno l'anagrafica con questi valori?</p>
                  {esito.anagrafica_da_confermare.map((v) => (
                    <label key={v.campo} className="flex items-center gap-2 mt-1">
                      <input type="checkbox" data-testid={`anag-${v.campo}`}
                        checked={conferme.some((c) => c.campo === v.campo)}
                        onChange={(e) => setConferme(e.target.checked ? [...conferme, v] : conferme.filter((c) => c.campo !== v.campo))}
                        className="accent-[#C9A84C]" />
                      <span>{v.campo}: {String(v.attuale)} → <strong>{String(v.nuovo)}</strong></span>
                    </label>
                  ))}
                  <button data-testid="anag-applica" onClick={applicaAnagrafica} disabled={!conferme.length}
                    className="mt-2 px-3 py-1.5 text-[12px] bg-[#1A2B4A] text-white rounded-md disabled:opacity-50">Aggiorna anagrafica</button>
                </div>
              )}
              {esito?.anagrafica_scritta?.length > 0 && (
                <p className="text-[11px] text-[#1F5C42]"><Check className="w-3 h-3 inline" /> Scritti in anagrafica (erano vuoti): {esito.anagrafica_scritta.join(", ")}</p>
              )}
            </div>
          )}
        </div>

        {passo === 3 && (
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#1A2B4A]/10">
            <button data-testid="doc-annulla" onClick={onClose} className="px-4 py-2 text-sm border border-[#1A2B4A]/20 rounded-md">Annulla</button>
            <button data-testid="doc-anteprima" onClick={() => genera(false)} disabled={!!busy}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-[#1A2B4A]/30 rounded-md disabled:opacity-60">
              {busy === "anteprima" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Anteprima
            </button>
            <button data-testid="doc-genera" onClick={() => genera(true)} disabled={!!busy || bloccoDoc}
              title={bloccoDoc ? "Spunta «Genero senza la documentazione completa» per procedere" : ""}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-md disabled:opacity-60">
              {busy === "salva" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Genera e salva
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
